import { DeltaBadge } from './DeltaBadge';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from './EmptyState';
import type { ChannelRow } from '@/hooks/useChannelMix';
import { formatINRCompact, formatNumberCompact } from '@/lib/format';
import { cn } from '@/lib/utils';

const COLORS: Record<string, string> = {
  Swiggy: 'bg-orange-500',
  Zomato: 'bg-rose-500',
  'Dine In': 'bg-emerald-500',
  'Pick Up': 'bg-sky-500',
  'Direct Delivery': 'bg-indigo-500',
  Uengage: 'bg-violet-500',
  Other: 'bg-muted-foreground',
};

interface Props {
  rows: ChannelRow[];
  loading?: boolean;
}

export function ChannelMix({ rows, loading }: Props) {
  if (loading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-3 w-full" />
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <EmptyState title="No channel data" description="No orders found for this period." />
    );
  }
  return (
    <div className="space-y-4">
      {/* Stacked share bar */}
      <div className="flex h-3 w-full overflow-hidden rounded-md">
        {rows.map((r) => (
          <div
            key={r.channel}
            className={cn('h-full', COLORS[r.channel] ?? 'bg-muted-foreground')}
            style={{ width: `${r.sharePct}%` }}
            title={`${r.channel} — ${r.sharePct.toFixed(1)}%`}
          />
        ))}
      </div>
      {/* Rows */}
      <ul className="divide-y divide-border/40">
        {rows.map((r) => (
          <li key={r.channel} className="flex items-center gap-3 py-2 text-sm">
            <span
              className={cn(
                'h-2.5 w-2.5 shrink-0 rounded-full',
                COLORS[r.channel] ?? 'bg-muted-foreground',
              )}
            />
            <span className="min-w-0 flex-1 truncate font-medium">{r.channel}</span>
            <span className="font-mono text-2xs text-muted-foreground">
              {formatNumberCompact(r.orders)} orders
            </span>
            <span className="w-20 text-right font-mono tabular-nums">
              {formatINRCompact(r.sales)}
            </span>
            <span className="w-12 text-right font-mono text-2xs text-muted-foreground">
              {r.sharePct.toFixed(0)}%
            </span>
            <DeltaBadge deltaPct={r.metric.deltaPct} comparable={r.metric.comparable} />
          </li>
        ))}
      </ul>
    </div>
  );
}
